import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import emailjs from '@emailjs/browser';

interface RequestFormData {
  projectName: string;
  sector: string;
  location: string;
  fundingRequired: string;
  stage: string;
  description: string;
  contactEmail: string;
}

export default function RequestForm() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<RequestFormData>({
    projectName: '',
    sector: '',
    location: '',
    fundingRequired: '',
    stage: '',
    description: '',
    contactEmail: ''
  });

  const [errors, setErrors] = useState({
    contactEmail: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  // List of common personal email domains to reject
  const personalEmailDomains = [
    'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'aol.com',
    'icloud.com', 'me.com', 'live.com', 'msn.com', 'ymail.com',
    'rocketmail.com', 'mail.com', 'protonmail.com', 'tutanota.com',
    'zoho.com', 'fastmail.com', 'hushmail.com', 'guerrillamail.com',
    'tempmail.org', '10minutemail.com', 'mailinator.com'
  ];

  const validateWorkEmail = (email: string): boolean => {
    if (!email) return false;
    
    // Basic email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return false;
    
    // Extract domain from email
    const domain = email.split('@')[1]?.toLowerCase();
    if (!domain) return false;
    
    // Check if it's a personal email domain
    return !personalEmailDomains.includes(domain);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const email = e.target.value;
    setFormData(prev => ({ ...prev, contactEmail: email }));
    
    // Clear previous errors
    setErrors({...errors, contactEmail: ''});
    
    // Validate email if it's not empty
    if (email && !validateWorkEmail(email)) {
      const domain = email.split('@')[1]?.toLowerCase();
      if (personalEmailDomains.includes(domain)) {
        setErrors({...errors, contactEmail: 'Please use your work email address. Personal email addresses are not accepted.'});
      } else if (!email.includes('@') || !email.includes('.')) {
        setErrors({...errors, contactEmail: 'Please enter a valid email address.'});
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate work email before submission
    if (!validateWorkEmail(formData.contactEmail)) {
      setErrors({...errors, contactEmail: 'Please use your work email address. Personal email addresses are not accepted.'});
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // EmailJS configuration - Replace these with your actual EmailJS credentials
      const serviceId = 'service_6xwjpuf';
      const templateId = 'template_t0j7rpq';
      const publicKey = 'rL5GacUiM5649BnKA';

      // Template parameters for EmailJS
      const templateParams = {
        to_email: 'info@hatomconsulting.com',
        project_name: formData.projectName,
        sector: formData.sector,
        location: formData.location,
        funding_required: `$${Number(formData.fundingRequired).toLocaleString()} USD`,
        stage: formData.stage,
        description: formData.description,
        contact_email: formData.contactEmail,
        subject: `New Capital Request: ${formData.projectName}`,
        reply_to: formData.contactEmail
      };

      // Send email using EmailJS
      await emailjs.send(serviceId, templateId, templateParams, publicKey);
      
      setSubmitStatus('success');
      
      // Navigate back after a short delay
      setTimeout(() => {
        navigate('/capital-request');
      }, 2000);
      
    } catch (error) {
      console.error('Email sending error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div>
      {submitStatus === 'success' && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-md">
          <p className="text-green-800">Thank you! Your capital request has been submitted successfully. We'll review it and get back to you soon.</p>
        </div>
      )}

      {submitStatus === 'error' && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-800">Sorry, there was an error submitting your request. Please try again or email us directly at info@hatomconsulting.com</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label htmlFor="projectName" className="block text-sm font-medium text-navy">
            Project Name *
          </label>
          <input
            type="text"
            id="projectName"
            name="projectName"
            required
            value={formData.projectName}
            onChange={handleChange}
            disabled={isSubmitting}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal focus:ring-teal"
          />
        </div>

        <div>
          <label htmlFor="sector" className="block text-sm font-medium text-navy">
            Sector *
          </label>
          <select
            id="sector"
            name="sector"
            required
            value={formData.sector}
            onChange={handleChange}
            disabled={isSubmitting}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal focus:ring-teal"
          >
            <option value="">Select a sector</option>
            <option value="Clean Energy">Clean Energy</option>
            <option value="Green Hydrogen">Green Hydrogen</option>
            <option value="Green Ammonia">Green Ammonia</option>
            <option value="Green Steel">Green Steel</option>
            <option value="Energy Storage">Energy Storage</option>
            <option value="Electric Mobility">Electric Mobility</option>
            <option value="Carbon Capture">Carbon Capture</option>
            <option value="Other">Other</option>
          </select>
        </div>

        <div>
          <label htmlFor="location" className="block text-sm font-medium text-navy">
            Location *
          </label>
          <input
            type="text"
            id="location"
            name="location"
            required
            value={formData.location}
            onChange={handleChange}
            disabled={isSubmitting}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal focus:ring-teal"
          />
        </div>

        <div>
          <label htmlFor="fundingRequired" className="block text-sm font-medium text-navy">
            Funding Required (USD) *
          </label>
          <input
            type="number"
            id="fundingRequired"
            name="fundingRequired"
            required
            value={formData.fundingRequired}
            onChange={handleChange}
            disabled={isSubmitting}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal focus:ring-teal"
            placeholder="e.g., 1000000"
          />
        </div>

        <div>
          <label htmlFor="stage" className="block text-sm font-medium text-navy">
            Project Stage *
          </label>
          <select
            id="stage"
            name="stage"
            required
            value={formData.stage}
            onChange={handleChange}
            disabled={isSubmitting}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal focus:ring-teal"
          >
            <option value="">Select a stage</option>
            <option value="Concept">Concept</option>
            <option value="Feasibility Study">Feasibility Study</option>
            <option value="Development">Development</option>
            <option value="Ready for Investment">Ready for Investment</option>
            <option value="Under Construction">Under Construction</option>
          </select>
        </div>

        <div>
          <label htmlFor="description" className="block text-sm font-medium text-navy">
            Project Description *
          </label>
          <textarea
            id="description"
            name="description"
            required
            value={formData.description}
            onChange={handleChange}
            disabled={isSubmitting}
            rows={4}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-teal focus:ring-teal"
            placeholder="Please provide a brief description of your project, including key objectives and expected outcomes."
          />
        </div>

        <div>
          <label htmlFor="contactEmail" className="block text-sm font-medium text-navy">
            Contact Email (Work Email) *
          </label>
          <input
            type="email"
            id="contactEmail"
            name="contactEmail"
            required
            value={formData.contactEmail}
            onChange={handleEmailChange}
            disabled={isSubmitting}
            className={`mt-1 block w-full rounded-md shadow-sm focus:border-teal focus:ring-teal ${
              errors.contactEmail ? 'border-red-300 bg-red-50' : 'border-gray-300'
            }`}
          />
          {errors.contactEmail && (
            <p className="mt-1 text-sm text-red-600">{errors.contactEmail}</p>
          )}
          <p className="mt-1 text-xs text-gray-500">
            Please use your company email address. Personal emails (Gmail, Yahoo, etc.) are not accepted.
          </p>
        </div>

        <div>
          <button
            type="submit"
            disabled={isSubmitting || !!errors.contactEmail}
            className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium transition-colors ${
              isSubmitting || !!errors.contactEmail
                ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                : 'text-white bg-teal hover:bg-teal-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500'
            }`}
          >
            {isSubmitting ? 'Submitting Request...' : 'Submit Request'}
          </button>
        </div>
      </form>
    </div>
  );
}
